import React, { useState, useEffect } from 'react';
import travelApiService from '../utils/travelApiService';
import './ShareLinkList.css';

const ShareLinkList = ({ planId, onClose }) => {
  const [shareLinks, setShareLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const permissionLabels = {
    view: { title: '읽기 전용', icon: '👁️', color: '#3b82f6' },
    edit: { title: '편집 가능', icon: '✏️', color: '#10b981' },
    admin: { title: '관리자', icon: '⚙️', color: '#f59e0b' },
  };

  useEffect(() => {
    fetchShareLinks();
  }, [planId]);

  const fetchShareLinks = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await travelApiService.getShareLinks(planId);

      if (response.success && response.data) {
        setShareLinks(response.data);
      } else {
        throw new Error(response.message || '공유 링크 목록을 불러올 수 없습니다');
      }
    } catch (error) {
      console.error('공유 링크 목록 조회 오류:', error);

      if (error.response?.status === 403) {
        setError('권한이 없습니다.');
      } else if (error.response?.status === 404) {
        setError('여행 계획을 찾을 수 없습니다.');
      } else {
        setError(error.message || '공유 링크 목록을 불러오는 중 오류가 발생했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async (token) => {
    const shareUrl = `${window.location.origin}/share/${token}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('✅ 링크가 클립보드에 복사되었습니다!');
    } catch (error) {
      console.error('클립보드 복사 실패:', error);
      alert('❌ 클립보드 복사에 실패했습니다.');
    }
  };

  const handleDeleteLink = async (token) => {
    if (!window.confirm('이 공유 링크를 삭제하시겠습니까?\n삭제된 링크는 더 이상 사용할 수 없습니다.')) {
      return;
    }

    try {
      const response = await travelApiService.deleteShareLink(planId, token);

      if (response.success) {
        alert('✅ 공유 링크가 삭제되었습니다.');
        // 목록 새로고침
        fetchShareLinks();
      } else {
        throw new Error(response.message || '공유 링크 삭제에 실패했습니다');
      }
    } catch (error) {
      console.error('공유 링크 삭제 오류:', error);

      if (error.response?.status === 403) {
        alert('❌ 권한이 없습니다.');
      } else if (error.response?.status === 404) {
        alert('❌ 공유 링크를 찾을 수 없습니다.');
      } else {
        alert(`❌ 삭제 실패: ${error.message || '알 수 없는 오류'}`);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '없음';
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getLinkStatus = (link) => {
    if (!link.is_active) {
      return { label: '비활성', color: '#6c757d', icon: '⚪' };
    }

    // 만료 확인
    if (link.expires_at) {
      const expiry = new Date(link.expires_at);
      const now = new Date();
      if (expiry <= now) {
        return { label: '만료됨', color: '#dc3545', icon: '🔴' };
      }
    }

    // 최대 사용 횟수 확인
    if (link.max_uses && link.used_count >= link.max_uses) {
      return { label: '최대 사용', color: '#6c757d', icon: '⚫' };
    }

    return { label: '활성', color: '#28a745', icon: '🟢' };
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content share-link-list-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔗 공유 링크 관리</h2>
          <button className="modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-section">
              <div className="loading-spinner"></div>
              <p>공유 링크를 불러오는 중...</p>
            </div>
          ) : error ? (
            <div className="error-section">
              <p className="error-text">❌ {error}</p>
              <button className="btn-primary" onClick={fetchShareLinks}>
                다시 시도
              </button>
            </div>
          ) : shareLinks.length === 0 ? (
            <div className="empty-section">
              <p className="empty-icon">🔗</p>
              <p className="empty-text">생성된 공유 링크가 없습니다.</p>
              <p className="empty-hint">공유 링크를 생성하여 친구와 여행 계획을 공유하세요!</p>
            </div>
          ) : (
            <div className="share-links-table">
              <table>
                <thead>
                  <tr>
                    <th>권한</th>
                    <th>만료일</th>
                    <th>사용 횟수</th>
                    <th>상태</th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {shareLinks.map((link) => {
                    const permission = permissionLabels[link.permission];
                    const status = getLinkStatus(link);

                    return (
                      <tr key={link.id}>
                        <td>
                          <span className="permission-badge" style={{ background: permission.color }}>
                            {permission.icon} {permission.title}
                          </span>
                        </td>
                        <td>{formatDate(link.expires_at)}</td>
                        <td>
                          {link.used_count} / {link.max_uses || '∞'}
                        </td>
                        <td>
                          <span className="status-badge" style={{ color: status.color }}>
                            {status.icon} {status.label}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-copy"
                              onClick={() => handleCopyLink(link.token)}
                              title="링크 복사"
                            >
                              📋
                            </button>
                            <button
                              className="btn-delete"
                              onClick={() => handleDeleteLink(link.token)}
                              title="링크 삭제"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareLinkList;
